import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Grid, CircularProgress, Alert, Breadcrumbs, Link, Chip, Divider
} from '@mui/material';
import { ArrowBack, LocalOffer, Discount, SettingsEthernet, LocationOn } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getCouponById } from '../../services/Api';

const ViewCouponDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [coupon, setCoupon] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCouponDetails = async () => {
            try {
                const response = await getCouponById(id);
                if (response.success || response.data) {
                    setCoupon(response.data || response);
                } else {
                    setError(response?.message || 'Failed to fetch coupon details');
                }
            } catch (err) {
                console.error(err);
                setError(err.response?.data?.message || 'Error fetching coupon details');
            } finally {
                setLoading(false);
            }
        };

        fetchCouponDetails();
    }, [id]);

    const formatDate = (d) => {
        if (!d) return '-';
        const date = new Date(d.replace(' ', 'T'));
        if (isNaN(date)) return d;
        return date.toLocaleDateString('en-IN', {
            weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
    if (!coupon) return <Alert severity="warning" sx={{ m: 4 }}>Coupon not found</Alert>;

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/coupons');
                        }}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <ArrowBack sx={{ mr: 0.5, fontSize: 'inherit' }} />
                        Coupons
                    </Link>
                    <Typography color="text.primary">Details</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocalOffer sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 300 }}>
                            {coupon.code}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Detailed view of the coupon's settings and thresholds
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <Discount color="action" /> Configuration
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <Typography variant="caption" color="text.secondary">Coupon Code</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '1.1rem' }}>{coupon.code}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <Typography variant="caption" color="text.secondary">Platform Type</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip label={coupon.type} size="small" sx={{ fontWeight: 600 }} />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <Typography variant="caption" color="text.secondary">Status</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip 
                                        label={coupon.isActive ? 'Active' : 'Inactive'} 
                                        size="small" 
                                        color={coupon.isActive ? 'success' : 'default'} 
                                    />
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12 }}><Divider /></Grid>

                            <Grid size={{ xs: 6, sm: 4 }}>
                                <Typography variant="caption" color="text.secondary">Discount Target</Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, textTransform: 'capitalize' }}>
                                    {coupon.discountType}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <Typography variant="caption" color="text.secondary">Discount Value</Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600, color: 'success.dark' }}>
                                    {coupon.discountType === 'percentage' ? `${coupon.discountValue}%` : `₹${coupon.discountValue}`}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <Typography variant="caption" color="text.secondary">Max Value Cap</Typography>
                                <Typography variant="body1" sx={{ mt: 0.5 }}>
                                    {coupon.maxValue ? `₹${coupon.maxValue}` : 'Unlimited'}
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12 }}><Divider /></Grid>

                            <Grid size={{ xs: 6, sm: 4 }}>
                                <Typography variant="caption" color="text.secondary">Start Date</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {formatDate(coupon.startDate)}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <Typography variant="caption" color="text.secondary">End Date</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {formatDate(coupon.endDate)}
                                </Typography>
                            </Grid>
                            
                            <Grid size={{ xs: 12 }}><Divider /></Grid>

                            {/* Delivery & Service Flags */}
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <Typography variant="caption" color="text.secondary">Free Delivery</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip label={coupon.isFreeDelivery ? 'Yes' : 'No'} size="small" variant={coupon.isFreeDelivery ? 'filled' : 'outlined'} color={coupon.isFreeDelivery ? 'primary' : 'default'} />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4 }}>
                                <Typography variant="caption" color="text.secondary">Free Service</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip label={coupon.isFreeService ? 'Yes' : 'No'} size="small" variant={coupon.isFreeService ? 'filled' : 'outlined'} color={coupon.isFreeService ? 'primary' : 'default'} />
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <SettingsEthernet color="action" /> Usage Details
                        </Typography>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" color="text.secondary">Minimum Order Amount</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 300, mt: 0.5 }}>
                                ₹{coupon.minOrderAmount || 0}
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" color="text.secondary">Total Times Used</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 300, mt: 0.5 }}>
                                {coupon.usedCount || 0}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Maximum Usage Limit</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 300, mt: 0.5 }}>
                                {coupon.usageLimit || '∞'}
                            </Typography>
                        </Box>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <LocationOn color="action" /> Assigned Locations
                        </Typography>
                        {coupon.locations && coupon.locations.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {coupon.locations.map(loc => (
                                    <Chip key={loc.id || loc._id} label={loc.name} size="small" color="primary" variant="outlined" />
                                ))}
                            </Box>
                        ) : coupon.locationIds && coupon.locationIds.length > 0 ? (
                            <Typography variant="body2" color="text.secondary">Associated with {coupon.locationIds.length} location(s)</Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary">No targeted locations</Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ViewCouponDetails;
